import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEFAULT_PAYMENT_WALL_CURRENCY = 'INR';

export interface PaymentWallPreflight {
  status: 'payment_required';
  featureKey: string;
  units: number;
  includedUnitsRemaining: number;
  paygOption: {
    label: string;
    unitPrice: number;
    currency: string;
    estimatedAmount: number;
  };
  plansOption: {
    label: string;
    recommendedPlanIds: string[];
  };
}

export interface PaymentWallConfirmation {
  outcome: 'payment_authorized' | 'plan_updated';
  title?: string;
  message?: string;
  amount?: number;
  currency?: string;
  planName?: string;
  planPriceMonthly?: number;
}

interface FullPagePaymentWallProps {
  preflight: PaymentWallPreflight;
  busy?: boolean;
  onPayNow: () => Promise<PaymentWallConfirmation> | PaymentWallConfirmation;
  onShowPlans?: () => void;
  onSwitchPlan?: (planId: string) => Promise<PaymentWallConfirmation> | PaymentWallConfirmation;
  onResumeBlockedAction: () => Promise<void> | void;
  onCancel: () => void;
}

type PaymentChoice = 'pay_now' | 'show_plans';

interface CatalogPlanLimit {
  featureKey: string;
  label: string;
  maxPerMonth: number | null;
}

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: number;
  currency?: 'INR' | 'USD';
  price?: number;
  status: 'active' | 'archived';
  sortOrder: number;
  limits: CatalogPlanLimit[];
}

interface PricingCatalogResponse {
  plans: CatalogPlan[];
}

const FEATURE_LABELS: Record<string, string> = {
  slideshow: 'Slideshow generation',
  shorts: 'Shorts generation',
  subtitle: 'Subtitle generation',
  youtube_posts: 'YouTube publishing',
};

function getFeatureLabel(featureKey: string): string {
  return FEATURE_LABELS[featureKey] || featureKey.replace(/_/g, ' ');
}

function normalizeCurrency(currency?: string): string {
  const rawCurrency = typeof currency === 'string' && currency.trim()
    ? currency.trim().toUpperCase()
    : DEFAULT_PAYMENT_WALL_CURRENCY;
  return rawCurrency;
}

function formatMoney(value: number, currency?: string): string {
  const normalizedCurrency = normalizeCurrency(currency);

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: DEFAULT_PAYMENT_WALL_CURRENCY,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

function formatLimit(maxPerMonth: number | null): string {
  return maxPerMonth === null ? 'Unlimited' : String(maxPerMonth);
}

function toErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function withDefaults(preflight: PaymentWallPreflight, confirmation: PaymentWallConfirmation): PaymentWallConfirmation {
  if (confirmation.outcome === 'plan_updated') {
    return {
      ...confirmation,
      title: confirmation.title || 'Plan updated',
      message: confirmation.message || 'Your plan update is confirmed. Resuming your action now.',
    };
  }

  return {
    ...confirmation,
    title: confirmation.title || 'Payment authorized',
    message: confirmation.message || 'Your payment is authorized. Resuming your action now.',
    amount: confirmation.amount ?? preflight.paygOption.estimatedAmount,
    currency: confirmation.currency ?? preflight.paygOption.currency,
  };
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

const FullPagePaymentWall: React.FC<FullPagePaymentWallProps> = ({
  preflight,
  busy = false,
  onPayNow,
  onShowPlans,
  onSwitchPlan,
  onResumeBlockedAction,
  onCancel,
}) => {
  const [choice, setChoice] = useState<PaymentChoice>('pay_now');
  const [showPlansPanel, setShowPlansPanel] = useState(false);
  const [plans, setPlans] = useState<CatalogPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [switchingPlanId, setSwitchingPlanId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PaymentWallConfirmation | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState(false);

  useEffect(() => {
    setChoice('pay_now');
    setShowPlansPanel(false);
    setPlansError(null);
    setSwitchingPlanId(null);
    setActionError(null);
    setConfirmation(null);
    setResumeError(null);
    setIsResuming(false);
  }, [preflight.featureKey, preflight.units, preflight.paygOption.estimatedAmount]);

  const loadPlans = async () => {
    if (plansLoading) return;
    setPlansLoading(true);
    setPlansError(null);
    try {
      const response = await fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to load plans');
      }

      const data = await response.json() as PricingCatalogResponse;
      const activePlans = (data.plans || [])
        .filter((plan) => plan.status === 'active')
        .sort((a, b) => a.sortOrder - b.sortOrder);
      setPlans(activePlans);
    } catch (err: unknown) {
      setPlansError(toErrorMessage(err, 'Failed to load plans'));
    } finally {
      setPlansLoading(false);
    }
  };

  const togglePlansPanel = async () => {
    const next = !showPlansPanel;
    setShowPlansPanel(next);
    if (next) {
      onShowPlans?.();
      if (plans.length === 0) {
        await loadPlans();
      }
    }
  };

  const runResumeFlow = async (nextConfirmation: PaymentWallConfirmation) => {
    setConfirmation(nextConfirmation);
    setActionError(null);
    setResumeError(null);
    setShowPlansPanel(false);

    await sleep(350);
    setIsResuming(true);

    try {
      await onResumeBlockedAction();
      onCancel();
    } catch (err: unknown) {
      setResumeError(toErrorMessage(err, 'Unable to resume your action right now.'));
    } finally {
      setIsResuming(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (choice === 'show_plans') {
      await togglePlansPanel();
      return;
    }

    setActionError(null);
    try {
      const next = await Promise.resolve(onPayNow());
      await runResumeFlow(withDefaults(preflight, next));
    } catch (err: unknown) {
      setActionError(toErrorMessage(err, 'Payment authorization failed'));
    }
  };

  const handleSwitchPlan = async (planId: string) => {
    if (!onSwitchPlan) return;

    setSwitchingPlanId(planId);
    setActionError(null);
    setPlansError(null);
    try {
      const next = await Promise.resolve(onSwitchPlan(planId));
      await runResumeFlow(withDefaults(preflight, next));
    } catch (err: unknown) {
      const message = toErrorMessage(err, 'Failed to switch plan');
      setActionError(message);
      setPlansError(message);
    } finally {
      setSwitchingPlanId(null);
    }
  };

  const retryResume = async () => {
    if (!confirmation || isResuming) return;
    await runResumeFlow(confirmation);
  };

  const primaryLabel = choice === 'show_plans'
    ? (showPlansPanel ? 'Hide plans' : preflight.plansOption.label)
    : `Pay ${formatMoney(preflight.paygOption.estimatedAmount, preflight.paygOption.currency)} and continue`;

  const recommendedPlanIds = new Set(preflight.plansOption.recommendedPlanIds);
  const canInteract = !busy && switchingPlanId === null && !isResuming;

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canInteract) return;
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="fpw-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="fpw-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fpw-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="fpw-modal-close"
          onClick={onCancel}
          disabled={!canInteract}
          aria-label="Close payment wall"
        >
          ×
        </button>

        <div className="fpw-shell">
          <div className="fpw-header">
            <p className="fpw-eyebrow">Usage limit reached</p>
            <h2 id="fpw-title">Complete this action securely</h2>
            <p>
              Your included usage for this feature is exhausted for the current billing period.
              Select how you want to continue.
            </p>
          </div>

          <div className="fpw-summary">
            <div className="fpw-summary-item">
              <span>Feature</span>
              <strong>{getFeatureLabel(preflight.featureKey)}</strong>
            </div>
            <div className="fpw-summary-item">
              <span>Requested now</span>
              <strong>
                {preflight.units} unit{preflight.units === 1 ? '' : 's'}
              </strong>
            </div>
            <div className="fpw-summary-item">
              <span>Included remaining</span>
              <strong>{preflight.includedUnitsRemaining}</strong>
            </div>
          </div>

          {confirmation ? (
            <div className="fpw-confirmation" role="status" aria-live="polite">
              <p className="fpw-confirmation-eyebrow">
                {confirmation.outcome === 'plan_updated' ? 'Plan confirmation' : 'Payment confirmation'}
              </p>
              <h3>{confirmation.title}</h3>
              <p className="fpw-confirmation-message">{confirmation.message}</p>

              <div className="fpw-confirmation-grid">
                <div className="fpw-confirmation-item">
                  <span>Feature</span>
                  <strong>{getFeatureLabel(preflight.featureKey)}</strong>
                </div>
                <div className="fpw-confirmation-item">
                  <span>Units</span>
                  <strong>{preflight.units}</strong>
                </div>
                {confirmation.outcome === 'payment_authorized' && (
                  <div className="fpw-confirmation-item">
                    <span>Amount</span>
                    <strong>{formatMoney(confirmation.amount || preflight.paygOption.estimatedAmount, confirmation.currency || preflight.paygOption.currency)}</strong>
                  </div>
                )}
                {confirmation.outcome === 'plan_updated' && (
                  <div className="fpw-confirmation-item">
                    <span>Plan</span>
                    <strong>
                      {confirmation.planName || 'Selected plan'}
                      {typeof confirmation.planPriceMonthly === 'number' && ` (${formatMoney(confirmation.planPriceMonthly)}/mo)`}
                    </strong>
                  </div>
                )}
              </div>

              {!resumeError && (
                <p className="fpw-confirmation-next-step">
                  {isResuming ? 'Confirming payment and resuming your action...' : 'Resuming your action now...'}
                </p>
              )}

              {resumeError && (
                <div className="fpw-confirmation-error">
                  <p>{resumeError}</p>
                  <div className="fpw-confirmation-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={retryResume}
                      disabled={!canInteract}
                    >
                      Retry
                    </button>
                    <a className="btn-secondary fpw-link-btn" href="/account/billing">
                      Go to billing
                    </a>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="fpw-options">
                <button
                  type="button"
                  className={`fpw-option-card ${choice === 'pay_now' ? 'selected' : ''}`}
                  onClick={() => setChoice('pay_now')}
                  disabled={!canInteract}
                >
                  <div className="fpw-option-head">
                    <h3>{preflight.paygOption.label}</h3>
                    <span className="fpw-pill">Instant</span>
                  </div>
                  <p className="fpw-price-line">
                    <span>{formatMoney(preflight.paygOption.unitPrice, preflight.paygOption.currency)} / unit</span>
                    <strong>{formatMoney(preflight.paygOption.estimatedAmount, preflight.paygOption.currency)}</strong>
                  </p>
                  <p className="fpw-note">Processing resumes immediately after authorization.</p>
                  <ul className="fpw-benefits-list">
                    <li>Continue immediately for this action</li>
                    <li>No monthly commitment</li>
                    <li>Exact charge shown upfront</li>
                  </ul>
                </button>

                <button
                  type="button"
                  className={`fpw-option-card ${choice === 'show_plans' ? 'selected' : ''}`}
                  onClick={() => setChoice('show_plans')}
                  disabled={!canInteract}
                >
                  <div className="fpw-option-head">
                    <h3>{preflight.plansOption.label}</h3>
                    <span className="fpw-pill fpw-pill-muted">Monthly</span>
                  </div>
                  <p className="fpw-note">Compare subscription plans before continuing this action.</p>
                  <ul className="fpw-benefits-list">
                    <li>Included monthly limits for frequent usage</li>
                    <li>Recommended plan guidance</li>
                    <li>Better value for repeated actions</li>
                  </ul>
                  {preflight.plansOption.recommendedPlanIds.length > 0 && (
                    <div className="fpw-recommended">
                      <span>
                        {preflight.plansOption.recommendedPlanIds.length} recommended plan{preflight.plansOption.recommendedPlanIds.length === 1 ? '' : 's'} available
                      </span>
                    </div>
                  )}
                </button>
              </div>

              {actionError && <div className="fpw-action-error">{actionError}</div>}

              <div className="fpw-actions">
                <button
                  className="btn-primary"
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={!canInteract}
                >
                  {busy ? 'Processing...' : primaryLabel}
                </button>
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={onCancel}
                  disabled={!canInteract}
                >
                  Cancel
                </button>
              </div>

              {showPlansPanel && (
                <div className="fpw-plans-panel">
                  <div className="fpw-plans-header">
                    <h3>Available plans</h3>
                    <p>Select a plan below to continue without leaving this page.</p>
                  </div>

                  {plansLoading && <div className="fpw-plans-loading">Loading plans...</div>}
                  {plansError && <div className="fpw-plans-error">{plansError}</div>}

                  {!plansLoading && !plansError && plans.length === 0 && (
                    <div className="fpw-plans-empty">No active plans are available right now.</div>
                  )}

                  {!plansLoading && !plansError && plans.length > 0 && (
                    <div className="fpw-plans-grid">
                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          className={`fpw-plan-card ${recommendedPlanIds.has(plan.id) ? 'recommended' : ''}`}
                        >
                          <div className="fpw-plan-card-head">
                            <h4>{plan.name}</h4>
                            {recommendedPlanIds.has(plan.id) && <span className="fpw-plan-tag">Recommended</span>}
                          </div>

                          <p className="fpw-plan-price">{formatMoney(plan.price ?? plan.priceMonthly, plan.currency ?? 'USD')} / month</p>

                          <ul className="fpw-plan-limits">
                            {plan.limits.slice(0, 5).map((limit) => (
                              <li key={`${plan.id}-${limit.featureKey}`}>
                                <span>{limit.label}</span>
                                <strong>{formatLimit(limit.maxPerMonth)}</strong>
                              </li>
                            ))}
                          </ul>

                          {onSwitchPlan && (
                            <button
                              type="button"
                              className="btn-secondary fpw-plan-select"
                              onClick={() => { void handleSwitchPlan(plan.id); }}
                              disabled={!canInteract || switchingPlanId === plan.id}
                            >
                              {switchingPlanId === plan.id ? 'Switching...' : 'Choose plan and continue'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullPagePaymentWall;
